/**
 * Conversion-optimized masterclass registration form.
 *
 * Short field set (name + email required), single high-contrast CTA,
 * inline validation, honeypot spam trap, and explicit success/error states.
 */
import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { trackMasterclassCTA } from "@/lib/masterclass-analytics";
import { trackEvent } from "@/lib/tracking";
import { storeMasterclassRegistration } from "@/lib/lead-storage";
import { syncLeadToCrm } from "@/lib/crm-sync";

import {
  trackFormStart,
  fieldTracker,
  trackFormValidationError,
  trackFormSubmit,
  trackFormSuccess,
  trackFormError,
} from "@/lib/form-analytics";
import { attributionProps, leadAttributionPayload } from "@/lib/utm";
import { attributionCoverage, newCorrelationId } from "@/lib/lead-attribution-quality";
import {
  AccessibleField,
  FormErrorSummary,
  focusFirstError,
} from "@/components/site/form-a11y";
import {
  registrationSchema,
  submitMasterclassRegistration,
  type MasterclassConfirmationState,
  type RegistrationInput,
} from "@/lib/masterclass-registration";

type Status = "idle" | "submitting" | "success" | "error";

const EMPTY: RegistrationInput = { name: "", email: "", phone: "", goal: "" };

const FIELD_ORDER = ["name", "email", "phone", "goal"];
const FIELD_LABELS: Record<string, string> = {
  name: "Full name",
  email: "Email",
  phone: "Phone",
  goal: "Biggest goal",
};


export function MasterclassRegistrationForm({ id = "register" }: { id?: string }) {
  const [values, setValues] = useState<RegistrationInput>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof RegistrationInput, string>>>({});
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState<string | null>(null);
  const honeypot = useRef<HTMLInputElement>(null);
  const inFlight = useRef(false);
  const navigate = useNavigate();

  const formIdentity = { formId: id, formName: "masterclass_registration" };
  const fields = useMemo(() => fieldTracker(formIdentity), [id]);

  const set = (key: keyof RegistrationInput) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    trackFormStart(formIdentity, { first_field: key });
    setValues((v) => ({ ...v, [key]: e.target.value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    // Ref guard, not just state: a fast double-click can fire two submits
    // within the same React render, where `status` is still "idle" in the
    // second handler's closure — which would create a duplicate lead.
    if (status === "submitting" || inFlight.current) return;
    setFormError(null);

    // Silent success for bots so they don't retry.
    if (honeypot.current?.value) {
      setStatus("success");
      return;
    }

    const parsed = registrationSchema.safeParse(values);
    if (!parsed.success) {
      const fieldErrors = parsed.error.flatten().fieldErrors;
      const next = {
        name: fieldErrors.name?.[0],
        email: fieldErrors.email?.[0],
        phone: fieldErrors.phone?.[0],
        goal: fieldErrors.goal?.[0],
      };
      setErrors(next);
      trackEvent("masterclass_register_invalid", { fields: Object.keys(fieldErrors).join(",") });
      trackFormValidationError(formIdentity, next);
      focusFirstError(id, FIELD_ORDER, next);
      return;
    }


    inFlight.current = true;
    setStatus("submitting");
    trackMasterclassCTA("masterclass_register_submit", { position: "registration_form" });
    trackFormSubmit(formIdentity, { position: "registration_form" });

    const correlationId = newCorrelationId("mc");

    try {
      const payload = {
        ...parsed.data,
        correlation_id: correlationId,
        source: "masterclass_registration_form",
        page: typeof window !== "undefined" ? window.location.pathname : "/masterclass",
        submitted_at: new Date().toISOString(),
        ...leadAttributionPayload(),
      };
      const confirmation = await submitMasterclassRegistration(payload);
      const coverage = attributionCoverage(payload);
      // Persist a copy for /admin/leads — never blocks the user. The row id is
      // emitted separately so the success event isn't delayed by the insert.
      void storeMasterclassRegistration(payload).then((leadId) => {
        trackEvent("lead_recorded", {
          form_name: formIdentity.formName,
          correlation_id: correlationId,
          lead_id: leadId ?? "merged_or_unavailable",
          ...coverage,
        });
      });
      syncLeadToCrm({
        event: "masterclass_registration",
        email: parsed.data.email,
        fullName: parsed.data.name,
        phone: parsed.data.phone,
        notes: parsed.data.goal,
        source: "masterclass_registration_form",
      });

      setStatus("success");
      setValues(EMPTY);
      trackEvent("masterclass_register_success", {
        correlation_id: correlationId,
        ...coverage,
        ...attributionProps(),
      });
      trackFormSuccess(formIdentity, {
        correlation_id: correlationId,
        ...coverage,
        ...attributionProps(),
      });
      navigate("/masterclass/thank-you", {
        replace: true,
        state: confirmation
          ? ({
              registration: confirmation,
              issuedAt: Date.now(),
              correlationId,
            } satisfies MasterclassConfirmationState)
          : undefined,
      });
    } catch (err) {
      // Only released on failure — after success we navigate away, and the
      // guard should keep holding until then.
      inFlight.current = false;
      setStatus("error");
      const message = err instanceof Error ? err.message : "Something went wrong. Please try again.";
      setFormError(message);
      trackEvent("masterclass_register_error", { message });
      trackFormError(formIdentity, message);
    }
  }

  if (status === "success") {
    return (
      <div
        id={id}
        role="status"
        aria-live="polite"
        tabIndex={-1}
        ref={(el) => el?.focus()}
        className="border border-gold/40 bg-gold/5 p-8 text-center focus:outline-none"
      >
        <CheckCircle2 className="h-8 w-8 text-gold mx-auto" aria-hidden="true" />

        <h3 className="font-display uppercase text-white text-xl mt-4">You're registered</h3>
        <p className="text-silver text-sm mt-3 max-w-md mx-auto leading-relaxed">
          Check your inbox for the confirmation and calendar invite. If it isn't
          there in a few minutes, check spam and add Dr. Sully to your contacts.
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-6 text-xs uppercase tracking-[0.2em] text-gold hover:underline"
        >
          Register someone else
        </button>
      </div>
    );
  }

  return (
    <form
      id={id}
      onSubmit={onSubmit}
      {...fields}
      noValidate
      aria-labelledby={`${id}-title`}
      aria-busy={status === "submitting"}
      className="border border-gold/20 bg-black/30 p-6 md:p-8"
    >
      <h3 id={`${id}-title`} className="font-display uppercase text-white text-xl">
        Save my free seat
      </h3>
      <p className="text-silver text-sm mt-2">
        Live 90 minutes with Dr. Sully. No cost, no pitch-only filler.
      </p>

      <FormErrorSummary formId={id} order={FIELD_ORDER} errors={errors} labels={FIELD_LABELS} />

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <AccessibleField formId={id} name="name" label="Full name" required error={errors.name}>
          {(p) => (
            <input
              {...p}
              type="text"
              autoComplete="name"
              value={values.name}
              onChange={set("name")}
              className="input-dark"
              placeholder="Jordan Reeves"
            />
          )}
        </AccessibleField>
        <AccessibleField formId={id} name="email" label="Email" required error={errors.email}>
          {(p) => (
            <input
              {...p}
              type="email"
              autoComplete="email"
              value={values.email}
              onChange={set("email")}
              className="input-dark"
              placeholder="you@company.com"
            />
          )}
        </AccessibleField>
        <AccessibleField formId={id} name="phone" label="Phone (optional)" error={errors.phone}>
          {(p) => (
            <input
              {...p}
              type="tel"
              autoComplete="tel"
              value={values.phone}
              onChange={set("phone")}
              className="input-dark"
              placeholder="(210) 555-0147"
            />
          )}
        </AccessibleField>
        <AccessibleField formId={id} name="goal" label="Biggest goal (optional)" error={errors.goal}>
          {(p) => (
            <input
              {...p}
              type="text"
              value={values.goal}
              onChange={set("goal")}
              className="input-dark"
              placeholder="Turn my expertise into an offer"
            />
          )}
        </AccessibleField>
      </div>

      {/* Honeypot — hidden from humans and assistive tech. */}
      <input
        ref={honeypot}
        type="text"
        name="company_website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="absolute left-[-9999px] h-0 w-0 opacity-0"
      />

      {formError && (
        <p
          role="alert"
          tabIndex={-1}
          ref={(el) => el?.focus()}
          className="mt-5 flex items-start gap-2 border border-destructive/40 bg-destructive/10 p-3 text-sm text-white focus:outline-none"
        >
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-destructive" aria-hidden="true" />
          <span>{formError}</span>
        </p>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-cta text-white font-display uppercase tracking-[0.2em] text-xs px-6 py-4 shadow-cta hover:brightness-110 transition-all disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        {status === "submitting" ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> Saving your seat…
          </>
        ) : (
          <>
            Register Free <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </>
        )}
      </button>

      <p aria-live="polite" className="sr-only">
        {status === "submitting" ? "Submitting your registration" : ""}
      </p>

      <p className="mt-3 text-[10px] uppercase tracking-[0.2em] text-silver text-center">
        Free · Replay included · Unsubscribe anytime
      </p>
    </form>
  );
}
